import { LinkObject, ClinicianLink } from "@teamprevent/common/model";
import * as moment from "moment";
import { Moment } from "moment/moment";

import { ReportDetails } from "../reportsCommon/reportsCommon";

export interface PpqReportModel extends ReportDetails {
    viewTaskId: string;
    caseReferenceNumber: number;
    title: string;
    firstName: string;
    lastName: string;
    // LocalDate
    dateOfBirth: Date;
    customer: LinkObject;
    orgLevel1: string;
    orgLevel2: string;
    orgLevel3: string;
    orgLevel4: string;
    jobTitle: string;
    secondaryReference: string;
    assessmentDate: Date;
    outcome: string;
    comments: string;
    clinician: ClinicianLink;
    covidQuestionsIncluded: boolean;
    covidQuestionsAnswered: boolean;
    covidFinalRiskGroup: string;
    isDefaultCustomerType: boolean;
    releaseReportComments: string;
}

export const PpqOutcome = {
    fit: "Fit",
    fitWithAdjustment: "FitWithAdjustment",
    unfit: "Unfit",
    temporarilyUnfit: "TemporarilyUnfit",
    temporarilyUnfitPendingFitForWork: "TemporarilyUnfitPendingFitForWork"
};

export interface PpqCovid19Outcome {
    covidFinalRiskGroup: string;
}

export interface AllImmsCompletedForRoleCertificateModel {
    employeeDetails: {
        title: string;
        firstName: string;
        lastName: string;
        dateOfBirth: Moment;
        customerName: string;
        customerNumber: string;
        orgLevel1: string;
        orgLevel2: string;
        orgLevel3: string;
        orgLevel4: string;
        jobTitle: string;
    };
    outcome: string;
    clinicalComments: string;
    immunisationClearDate: Moment;
    completedByName: string;
}

export const parseCertificateModel = (model: AllImmsCompletedForRoleCertificateModel): AllImmsCompletedForRoleCertificateModel => ({
    ...model,
    immunisationClearDate: moment(model.immunisationClearDate),
    employeeDetails: {
        ...model.employeeDetails,
        dateOfBirth: moment(model.employeeDetails.dateOfBirth)
    }
});

// NOTE: The const values used below are being converted back to enum values in Commands\Ppq\Covid19\CovidRiskAgeCalculator.cs
// Hence any changes to const values here should be covered by corresponding changes in the enums used in CovidRiskAgeCalculator.cs
export const GENDER_AT_BIRTH_OPTIONS = [
    "Male",
    "Female"
].map(o => ({ text: o, value: o }));

export const ETHINICITY_OPTIONS = [
    "White",
    "Asian or Asian British",
    "Black",
    "Mixed",
    "Other non-white"
].map(o => ({ text: o, value: o }));

export const ASTHMA_OPTIONS = [
    "None",
    "Mild",
    "Severe"
].map(o => ({ text: o, value: o }));

export const DIABETES_OPTIONS = [
    "None",
    "Type 1 HbA1c less than or equal to 58 mmol/mol in past year",
    "Type 1 HBA1c greater than 58 mmol/mol in past year",
    "Type 1 HbA1c unknown",
    "Type 2 and other HbA1c less than or equal to 58 mmol/mol in past year",
    "Type 2 and other HbA1c greater than 58 mmol/mol in past year",
    "Type 2 and other HbA1c unknown",
].map(o => ({ text: o, value: o }));

export const CHRONIC_KIDNEY_DISEASE_OPTIONS = [
    "None",
    "Estimated GFR 30-60 mL/min",
    "Estimated GFR < 30 mL/min (includes patients on dialysis)",
].map(o => ({ text: o, value: o }));

export const CANCER_OPTIONS = [
    "None",
    "Diagnosed less than 1 year ago",
    "Diagnosed 1-4.9 years ago",
    "Diagnosed greater than or equal to 5 years ago",
].map(o => ({ text: o, value: o }));

export const HEART_OPTIONS = [
    "None",
    "Heart failure",
    "Other chronic heart disease",
].map(o => ({ text: o, value: o }));

export const COVID19_VACCINE_OPTIONS = [
    "Moderna",
    "AstraZeneca",
    "Pfizer",
    "Unknown"
].map(o => ({ text: o, value: o }));

export const N_A = "N/A";

export const N_A_VALUE = { id: N_A, name: N_A };

export const N_A_OPTION = { key: N_A, value: N_A, text: N_A };
